import React from "react";
import { Paper, Stack, Typography, Box, Skeleton } from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import InfoButton from "../Clinical/InfoButton";
import CMStatCards from "../CareManager/CMStatCards";
import UnmanagedBHTable from "./UnmanagedBHTable";

/**
 * Panel 5: Unmanaged BH Patients.
 * 3 stat cards + patient table prioritized by BH severity.
 * Most actionable panel — shows high-severity patients without CM.
 */
export default function PanelUnmanagedBH({ data, loading, onCreateTask, helpContent }) {
  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Skeleton variant="rectangular" animation="wave" height={300} sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  const patients = data?.patients || [];
  const hasPatients = patients.length > 0;

  const statItems = [
    { label: "Severely Complex", value: data?.severelyComplexNoCM, color: "#B71C1C", bgColor: "#FFEBEE" },
    { label: "Complex SPMI", value: data?.complexSPMINoCM, color: "#D32F2F", bgColor: "#FFEBEE" },
    { label: "Complex Any BH", value: data?.complexAnyBHNoCM, color: "#E65100", bgColor: "#FFF3E0" },
  ];

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 1.5,
              bgcolor: "#FFF3E0", color: "#E65100",
            }}
          >
            <ReportProblemIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Unmanaged BH Patients</Typography>
            <Typography variant="caption" color="text.secondary">High-severity BH patients without active care management</Typography>
          </Box>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Stat Cards */}
        <CMStatCards items={statItems} />

        {/* Total unmanaged banner */}
        {data?.totalHighComplexityNoCM > 0 && (
          <Box sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, bgcolor: "#FEE2E2", textAlign: "center" }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: "#DC2626" }}>
              {data.totalHighComplexityNoCM} high-complexity patients without CM (of {data.totalUnmanaged || 0} total unmanaged)
            </Typography>
          </Box>
        )}

        {/* Table */}
        {hasPatients ? (
          <UnmanagedBHTable patients={patients} onCreateTask={onCreateTask} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}>
            No unmanaged high-severity patients
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
